import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarClock, Disc3, SlidersHorizontal, Video } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { BeatCard } from "@/components/BeatCard";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { ChatFaqCarousel } from "@/components/ChatFaqCarousel";
import { ServiceDetailModal } from "@/components/ServiceDetailModal";

const HERO_IMG = "/api/files/beatsbyatl/media/still_10.jpg";
const HERO_LINES = ["ATLANTA'S", "PREMIUM BEAT", "& PRODUCTION HUB"];
const HERO_VIDEOS = [
  "/api/files/beatsbyatl/videos/04d7cf88e08e.mp4",
  "/api/files/beatsbyatl/media/hero_loop_2.mp4",
  "/api/files/beatsbyatl/media/hero_loop_3.mp4",
  "/api/files/beatsbyatl/media/hero_loop_4.mp4",
];
// Music video / editing showcase loops (mid-page band). New uploads get mint-graded and appended here.
const SHOWCASE_VIDEOS = [
  "/api/files/beatsbyatl/media/showcase_2.mp4",
  "/api/files/beatsbyatl/media/showcase_3.mp4",
  "/api/files/beatsbyatl/media/showcase_5.mp4",
  "/api/files/beatsbyatl/media/showcase_1.mp4",
  "/api/files/beatsbyatl/media/showcase_4.mp4",
  "/api/files/beatsbyatl/media/studio_zoom.mp4",
];

const HOME_LAB_CLIPS = [
  { id: "lab-session-1", title: "In session", url: "/api/files/beatsbyatl/media/services_lab_1.mp4" },
  {
    id: "lab-session-2",
    title: "At the desk",
    blurb: "Recording with high-tier equipment, & the skills to use them — artists can get a studio-quality recording with a friendlier price entry.",
    url: "/api/files/beatsbyatl/media/services_lab_2.mp4",
  },
];


const VideoLoopRotation = ({ videos, activeOpacity = "opacity-50", testidPrefix = "hero-video" }) => {
  const [idx, setIdx] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    if (videos.length < 2) return undefined;
    const timer = setInterval(() => setIdx((c) => (c + 1) % videos.length), 12000);
    return () => clearInterval(timer);
  }, [videos.length]);

  useEffect(() => {
    refs.current.forEach((v, i) => {
      if (!v) return;
      if (i === idx) {
        v.currentTime = 0;
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [idx]);

  return (
    <>
      {videos.map((src, i) => (
        <video
          key={src}
          ref={(el) => { refs.current[i] = el; }}
          src={fileUrl(src)}
          muted
          loop
          playsInline
          autoPlay={i === 0}
          preload={i <= 1 ? "auto" : "metadata"}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
            i === idx ? activeOpacity : "opacity-0"
          }`}
          data-testid={i === 0 ? `${testidPrefix}-loop` : `${testidPrefix}-${i}`}
        />
      ))}
    </>
  );
};

const HeroVideoRotation = () => <VideoLoopRotation videos={HERO_VIDEOS} />;

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const SERVICE_ROUTES = {
  "audio-eng": { to: "/booking", cta: "Book Session" },
  videography: { to: "/videography", cta: "Videography" },
  "custom-beat": { to: "/beats", cta: "Beats Store" },
  photography: { to: "/photography", cta: "Photography" },
  vlogging: { to: "/videography", cta: "Videography" },
};

const serviceDestination = (s) => {
  const byId = SERVICE_ROUTES[s.id];
  if (byId) return byId;
  const title = (s.title || "").toLowerCase();
  if (title.includes("photo")) return SERVICE_ROUTES.photography;
  if (title.includes("video") || title.includes("vlog") || title.includes("content")) return SERVICE_ROUTES.videography;
  if (title.includes("mix") || title.includes("master") || title.includes("record") || title.includes("studio"))
    return SERVICE_ROUTES["audio-eng"];
  if (title.includes("beat") || title.includes("custom")) return SERVICE_ROUTES["custom-beat"];
  return { to: "/booking", cta: "Book" };
};

const SERVICE_ICONS = { "audio-eng": SlidersHorizontal, videography: Video, "custom-beat": Disc3 };

export default function Home() {
  const [beats, setBeats] = useState([]);
  const [soldBeats, setSoldBeats] = useState([]);
  const [services, setServices] = useState([]);
  const [openService, setOpenService] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    api.get("/beats").then((r) => {
      setBeats(r.data.filter((b) => !b.sold_exclusive).slice(0, 3));
      setSoldBeats(r.data.filter((b) => b.sold_exclusive));
    }).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="relative flex min-h-[92vh] items-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-24">
          <img src={fileUrl(HERO_IMG)} alt="In the studio" className="h-full w-full object-cover opacity-30" />
          <HeroVideoRotation />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0E] via-[#0B0B0E]/40 to-[#0B0B0E]/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0E]/80 via-transparent to-transparent" />

        <motion.div style={{ opacity: contentFade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-6 lg:px-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-emerald-300"
            data-testid="hero-eyebrow"
          >
            Atlanta, GA
          </motion.p>
          <h1 className="font-heading mt-6 text-4xl font-extrabold leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {HERO_LINES.map((line, i) => (
              <MaskedLine key={line} delay={0.35 + i * 0.14}>
                {i === 2 ? <span className="text-mint-gradient">{line}</span> : line}
              </MaskedLine>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
            data-testid="hero-subheadline"
          >
            Hard-hitting Trap, R&B, Drill & Melodic beats crafted in Atlanta — plus mixing, mastering, videography and studio time, all under one roof.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/beats"
              data-testid="hero-cta-beats"
              className="group flex items-center gap-3 rounded-full bg-emerald-300 px-8 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:bg-emerald-200 hover:shadow-[0_0_40px_rgba(110,231,183,0.35)]"
            >
              Explore Beat Store
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/booking"
              data-testid="hero-cta-booking"
              className="flex items-center gap-3 rounded-full border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:border-emerald-300/60 hover:text-emerald-200"
            >
              <CalendarClock size={16} /> Book Studio Session
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <Reveal>
          <div className="flex items-end justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Fresh out the lab</p>
              <h2 className="font-heading mt-3 text-2xl font-bold tracking-tight text-white sm:text-4xl">Featured Beats</h2>
            </div>
            <Link to="/beats" className="group flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors hover:text-emerald-200" data-testid="featured-view-all-link">
              View all <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="featured-beats-grid">
          {beats.map((b, i) => (
            <BeatCard key={b.id} beat={b} list={beats} index={i} />
          ))}
        </div>
        {beats.length === 0 && (
          <p className="mt-10 text-center text-sm text-zinc-600">Beats are loading into the store…</p>
        )}
      </section>

      {soldBeats.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 sm:pb-20 lg:px-8" data-testid="home-sold-strip">
          <Reveal>
            <div className="flex items-end justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-red-400">Off the market</p>
                <h2 className="font-heading mt-3 text-xl font-bold tracking-tight text-white sm:text-2xl">Recently Sold Exclusives</h2>
              </div>
              <Link to="/beats" className="group flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors hover:text-emerald-200" data-testid="sold-strip-store-link">
                Beat store <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-6 flex gap-4 overflow-x-auto pb-2" data-testid="home-sold-scroll">
            {soldBeats.map((b) => (
              <div key={b.id} data-testid={`home-sold-item-${b.id.slice(0, 8)}`}
                className="flex w-64 shrink-0 items-center gap-3 rounded-2xl border border-red-500/20 bg-[#141419] p-3">
                <img src={fileUrl(b.cover_url)} alt={b.title} loading="lazy" className="h-14 w-14 rounded-xl object-cover opacity-70 grayscale" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-zinc-300">{b.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">{b.genre} · {b.bpm} BPM</p>
                </div>
                <span className="flex shrink-0 items-center gap-1 rounded-full bg-red-500/15 px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-wider text-red-400">
                  SOLD
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <ChatFaqCarousel />

      <section id="services" className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 sm:pb-24 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Beyond the beats</p>
          <h2 className="font-heading mt-3 text-2xl font-bold tracking-tight text-white sm:text-4xl">Studio Services</h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-zinc-400">
            From the first loop to the final master — and the video to match. Tap a service to see work, pricing and book.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-services-bento">
          {services.map((s, i) => {
            const Icon = SERVICE_ICONS[s.id] || Disc3;
            const dest = serviceDestination(s);
            return (
              <Reveal key={s.id} delay={(i % 3) * 0.08}>
                <button
                  type="button"
                  onClick={() => setOpenService({ service: s, destination: dest })}
                  data-testid={`home-service-card-${s.id}`}
                  className="group relative block h-full w-full overflow-hidden rounded-2xl border border-white/10 text-left transition-colors hover:border-emerald-300/40"
                  onMouseEnter={(e) => {
                    const v = e.currentTarget.querySelector("video[data-hover-clip]");
                    if (v) {
                      v.currentTime = 0;
                      v.play().catch(() => {});
                    }
                  }}
                  onMouseLeave={(e) => {
                    const v = e.currentTarget.querySelector("video[data-hover-clip]");
                    if (v) v.pause();
                  }}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={fileUrl(s.image)}
                      alt={s.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {s.hover_video && (
                      <video
                        data-hover-clip
                        data-testid={`home-service-hover-clip-${s.id}`}
                        src={fileUrl(s.hover_video)}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                    <div className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-emerald-300/40 bg-black/50 text-emerald-200 backdrop-blur-md">
                      <Icon size={15} />
                    </div>
                  </div>
                  <div className="space-y-2 bg-[#141419] p-5">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                      {s.price_label}
                    </p>
                    <h3 className="font-heading text-lg font-bold tracking-tight text-white">{s.title}</h3>
                    <p className="line-clamp-2 text-xs leading-relaxed text-zinc-400">{s.description}</p>
                    <span
                      data-testid={`home-service-cta-${s.id}`}
                      className="mt-3 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-200 transition-colors group-hover:text-emerald-300"
                    >
                      Details & {dest.cta} <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 sm:pb-24 lg:px-8" data-testid="about-strip">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -left-3 -top-3 h-full w-full rounded-3xl border border-emerald-300/40" />
              <div className="group relative overflow-hidden rounded-3xl">
                <img
                  src={fileUrl("/api/files/beatsbyatl/media/producer_studio.webp")}
                  alt="PRODRUSHI in the studio"
                  loading="lazy"
                  className="spotlight-img aspect-[4/5] w-full object-cover"
                  data-testid="about-producer-photo"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <p className="absolute bottom-4 left-4 rounded-full bg-black/60 px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest text-emerald-200 backdrop-blur-md">
                  Atlanta
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 lg:pl-8">
            <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">The face behind the beats</p>
            <h2 className="font-heading mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Cooked up in Atlanta,<br />built for your voice.
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-zinc-400 sm:text-base">
              Every beat in the vault is produced, mixed and mastered in-house at ATL Metro Sound Lab. No loops packs,
              no middlemen — you lease directly from the person who made it, and the same hands are available for your
              mix, your master, and your video.
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              {[["100%", "In-house production"], ["48h", "Custom beat turnaround"], ["ATL", "Born & raised"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-heading text-2xl font-extrabold text-mint-gradient">{v}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{l}</p>
                </div>
              ))}
            </div>
            <Link to="/contact" data-testid="about-contact-link"
              className="mt-8 inline-flex items-center gap-3 rounded-full border border-emerald-300/40 px-7 py-3.5 text-xs font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-300 hover:text-black">
              Work With Me <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8" data-testid="home-behind-boards">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Behind the boards</p>
          <h2 className="font-heading mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">Inside the Lab</h2>
        </Reveal>
        <div className="mt-8 grid gap-6 sm:grid-cols-2" data-testid="home-lab-videos">
          {HOME_LAB_CLIPS.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.1}>
              <figure
                className="group relative overflow-hidden rounded-2xl border border-white/10"
                data-testid={`home-lab-clip-${c.id}`}
              >
                <video
                  src={fileUrl(c.url)}
                  autoPlay muted loop playsInline
                  className="aspect-video w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <figcaption className="absolute bottom-4 left-5 right-5">
                  <span className="font-mono text-[11px] uppercase tracking-widest text-emerald-200">
                    {c.title}
                  </span>
                  {c.blurb && (
                    <span className="mt-1 block max-w-md text-xs leading-snug text-zinc-300" data-testid={`home-lab-blurb-${c.id}`}>
                      {c.blurb}
                    </span>
                  )}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative flex h-[62vh] items-center justify-center overflow-hidden border-y border-white/10" data-testid="showcase-section">
        <VideoLoopRotation videos={SHOWCASE_VIDEOS} activeOpacity="opacity-100" testidPrefix="showcase-video" />
        <div className="absolute inset-0 bg-[#0B0B0E]/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0E] via-transparent to-[#0B0B0E]" />
        <Reveal className="relative z-10 px-4 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-emerald-300">Visuals from the lab</p>
          <h2 className="font-heading mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
            Beats, sessions &amp; cinema-grade video
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-zinc-300 sm:text-base">
            Every record leaves with a look — in-studio performance shots, 4K music videos and visualizers shot right here in Atlanta.
          </p>
          <Link to="/videography" data-testid="showcase-services-link"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-emerald-300 px-8 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:bg-emerald-200 hover:shadow-[0_0_40px_rgba(110,231,183,0.35)]">
            See Videography <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-emerald-300/20 bg-gradient-to-br from-[#1E1E26] to-[#0B0B0E] p-10 sm:p-16" data-testid="home-booking-cta">
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-300/10 blur-3xl" />
            <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Blackroom Studios</p>
            <h2 className="font-heading mt-4 max-w-2xl text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Lock in your session. Leave with a record.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-400 sm:text-base">
              Recording, mixing and video shoots in the heart of Atlanta. Secure your slot with a $50 deposit — pick your date and time online.
            </p>
            <Link to="/booking" data-testid="home-booking-cta-button"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-emerald-300 px-8 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:bg-emerald-200 hover:shadow-[0_0_40px_rgba(110,231,183,0.35)]">
              Book a Session <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>

      <ServiceDetailModal
        service={openService?.service}
        destination={openService?.destination}
        onClose={() => setOpenService(null)}
      />
    </div>
  );
}
